import { Link, useRouter } from "@tanstack/react-router";
import { cn } from "@/lib/utils";
import { EMS_TAB_BAR, emsTabClass } from "@/components/ui/ems-tabs";
import { getActiveDomainAndTab, SubTabItem, DomainItem } from "@/config/navigation";
import {
    LayoutDashboard, ShoppingCart, FileText, Settings, Factory, Package, ClipboardCheck, Truck, Box,
    ChevronDown, ChevronRight, Users, ShieldCheck, Database, Users2, Layers, Scissors, Activity,
    Warehouse, ClipboardList, Cpu, Briefcase, HelpCircle, BookOpen, History, Menu, type LucideIcon,
    CheckCircle2, AlertTriangle, FileWarning, Microscope, Container,
    KanbanSquare, MonitorPlay, ArrowRightLeft, CreditCard, PieChart, BarChart, GitBranch, Printer,
    MessageSquarePlus, ScanBarcode, Trash2, Percent, PenTool, Copy, Wrench, FileSpreadsheet, PackageCheck, Calculator
} from "lucide-react";
import { useRef, useEffect } from "react";
import { useUserPreferences } from "@/stores/useUserPreferences";
import { useRoleAccess } from "@/hooks/useRoleAccess";

const IconMap: Record<string, LucideIcon> = {
    LayoutDashboard, ShoppingCart, FileText, Settings, Factory, Package, ClipboardCheck, Truck, Box,
    Users, ShieldCheck, Database, Users2, Layers, Scissors, Activity, Warehouse, ClipboardList, Cpu, Briefcase,
    HelpCircle, BookOpen, History, Menu, CheckCircle2, AlertTriangle, FileWarning, Microscope, Container,
    KanbanSquare, MonitorPlay, ArrowRightLeft, CreditCard, PieChart, BarChart, GitBranch, Printer,
    MessageSquarePlus, ScanBarcode, Trash2, Percent, PenTool, Copy, Wrench, FileSpreadsheet, PackageCheck, Calculator
};

export function SubHeaderNav() {
    const router = useRouter();
    const pathname = router.state.location.pathname;
    const searchObj = router.state.location.search as any;
    const menuLayout = useUserPreferences((s) => s.menuLayout || 'treeview');
    const { hasPermission } = useRoleAccess();

    let searchString = "";
    if (searchObj && typeof searchObj === "object" && Object.keys(searchObj).length > 0) {
        const params = new URLSearchParams();
        Object.entries(searchObj).forEach(([k, v]) => {
            if (v !== undefined && v !== null) params.append(k, String(v));
        });
        searchString = `?${params.toString()}`;
    }

    const { activeDomain, activeTab } = getActiveDomainAndTab(pathname, searchString);
    const scrollRef = useRef<HTMLDivElement>(null);

    // Auto-scroll active tab into view on mobile / desktop overflow
    useEffect(() => {
        if (scrollRef.current && activeTab) {
            const activeEl = scrollRef.current.querySelector('[data-active="true"]');
            if (activeEl) {
                activeEl.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
            }
        }
    }, [pathname, searchString, activeTab]);

    // If user prefers TreeView vertical menu, horizontal SubHeaderNav is hidden
    if (menuLayout === 'treeview') {
        return null;
    }

    const visibleTabs = (activeDomain?.tabs || []).filter(
        (tab) => !tab.moduleId || hasPermission(tab.moduleId, "view"),
    );

    if (!activeDomain || visibleTabs.length === 0) {
        return null;
    }

    return (
        <div className="w-full bg-white border-t border-slate-200/80 shadow-2xs transition-all z-15 select-none">
            <div
                ref={scrollRef}
                className="max-w-[1920px] mx-auto px-2 md:px-4 overflow-x-auto no-scrollbar scroll-smooth pt-1"
            >
                {/* Tab chuẩn EMS: thẻ + gạch chân đậm khi chọn */}
                <div className={cn(EMS_TAB_BAR, "min-w-max border-b-0")}>
                    {visibleTabs.map((tab) => {
                        const IconComponent = tab.icon ? IconMap[tab.icon] || FileText : FileText;

                        // Precise Tab Active Matching Engine
                        const isTabActive = activeTab?.href === tab.href || (() => {
                            if (tab.href.includes('?')) {
                                const [tabPath, tabSearch] = tab.href.split('?');
                                return pathname.replace(/\/$/, '') === tabPath.replace(/\/$/, '') && searchString.includes(tabSearch);
                            }
                            const cleanPath = pathname.replace(/\/$/, '') || '/';
                            const tabCleanHref = tab.href.replace(/\/$/, '');
                            if (cleanPath === tabCleanHref && !searchString.includes('domain=procurement')) return true;
                            if (tabCleanHref !== '/' && cleanPath.startsWith(tabCleanHref + '/')) return true;
                            return false;
                        })();

                        return (
                            <Link
                                key={tab.title + tab.href}
                                to={tab.href as any}
                                data-active={isTabActive ? "true" : "false"}
                                aria-current={isTabActive ? "page" : undefined}
                                className={emsTabClass(isTabActive, { size: "sm" })}
                            >
                                <IconComponent className={cn("w-3.5 h-3.5 shrink-0", isTabActive ? "text-primary" : "text-slate-400")} />
                                <span>{tab.title}</span>
                                {tab.badge && (
                                    <span className={cn(
                                        "ml-0.5 px-1.5 py-0.2 text-[9px] font-bold rounded-full",
                                        isTabActive ? "bg-primary/10 text-primary" : "bg-slate-100 text-slate-600"
                                    )}>
                                        {tab.badge}
                                    </span>
                                )}
                            </Link>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
